import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import QuestionHeader from "../../../components/common/questionHeader";
import QuestionSubHeader from "../../../components/common/questionSubHeader";
import ParticipantPostInventoryQuestions from "../../../components/participants/ictPostInventory/ictPostInventoryDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  IctDetailsErrorSelector,
  IctDetailsLoadingSelector,
  IctInventoryPartialAnswersDataSelector,
  IctInventoryDataSelector,
} from "../../../redux/selectors/ictDetailsSelector";
import { IctDetailsAction } from "../../../redux/action";
import { useNavigate, useParams } from "react-router-dom";
import {
  setIctDetailsError,
  setIctInventoryData,
} from "../../../redux/slice/ictDetailsSlice";
import Loader from "../../../components/common/spinner/WhiteSpinner";
import ErrorPage from "../../../components/errors/errorPage";

function ParticipantPostInventoryDetailsPage() {
  const navigate = useNavigate();
  const { inventory_id } = useParams();
  const XAM_TYPE = 5;
  const { t } = useTranslation();
  const ictPostInventoryDetailsData = useSelector(IctInventoryDataSelector);
  const ictDetailsErrorData = useSelector(IctDetailsErrorSelector);
  const isLoading = useSelector(IctDetailsLoadingSelector);
  const postInventoryPartialAnswers = useSelector(
    IctInventoryPartialAnswersDataSelector,
  );
  const dispatch = useDispatch();
  const blockPartialSaveRef = useRef(false);

  useEffect(() => {
    dispatch(setIctDetailsError({}));
    dispatch(setIctInventoryData({}));
    dispatch(IctDetailsAction.getIctInventoryDetailsAsync(inventory_id, t));
  }, [inventory_id, dispatch, t]);

  useEffect(() => {
    if (ictDetailsErrorData?.data?.error?.error_code === 4043) {
      navigate(`/participants/ict/post-inventories/${inventory_id}/results`);
    }
  }, [ictDetailsErrorData, inventory_id, navigate]);

  const setAddAnswer = useCallback(
    (inventoryId, data) => {
      blockPartialSaveRef.current = true;
      dispatch(
        IctDetailsAction.addIctInventoryAnswerAsync(
          inventoryId,
          data,
          t,
          navigate,
        `/participants/ict/post-inventories/${inventoryId}/results`,)
      )
      .then((success) => {
        if (!success) {
          blockPartialSaveRef.current = false;
        }
      });
    },
    [dispatch, navigate, t],
  );

  const setPartialAnswer = useCallback(
    (inventoryId, data) => {
      if (blockPartialSaveRef.current) {
        return Promise.resolve({ success: true });
      }
      return dispatch(
        IctDetailsAction.addIctInventoryPartialAnswersAsync(inventoryId, data, t),
      );
    },
    [dispatch, t],
  );

  const setAnswerPage = useCallback(
    (dataToSave) =>
      dispatch(
        IctDetailsAction.UpdateIctInventoryAnswerPageAsync(
          inventory_id,
          dataToSave,
          t,
        ),
      ),
    [dispatch, inventory_id, t],
  );

  const getCurrentPage = () => {
    return ictPostInventoryDetailsData?.current_page;
  };

  useEffect(() => {
    dispatch(
      IctDetailsAction.getIctInventoryPartialAnswersAsync(inventory_id, t),
    );
  }, [dispatch, inventory_id, t]);

  return (
    <>
      {isLoading && <Loader />}
      {ictDetailsErrorData?.status === 404 ||
      (ictDetailsErrorData?.status === 403 &&
        ictDetailsErrorData?.data?.error?.error_code !== 4043) ? (
        <>
          <ErrorPage status={ictDetailsErrorData?.status} />
        </>
      ) : (
        <div className="main_bg d-flex flex-column min-vh-100">
          <section className="mb-5">
            <QuestionHeader type={XAM_TYPE} />
            <QuestionSubHeader
              data={ictPostInventoryDetailsData}
              type={XAM_TYPE}
              serviceType="ict"
            />
            <ParticipantPostInventoryQuestions
              setAnswer={setAddAnswer}
              setAnswerPage={setAnswerPage}
              currentPage={getCurrentPage()}
              data={ictPostInventoryDetailsData}
              PartialAnswers={postInventoryPartialAnswers?.data}
              setPartialAnswer={setPartialAnswer}
            />
          </section>
        </div>
      )}
    </>
  );
}

export default ParticipantPostInventoryDetailsPage;
